import {HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {ClassProvider, Inject, Injectable, Optional} from '@angular/core';
import {from, Observable, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {ResponseStatus} from '../models/common.enums';
import {AuthService} from '../services/auth.service';
import * as Models from '../models/index';
import {CustomModalService} from '../services/custom-modal.service';
import {GeneralRoutesConfig} from '../configs/general-routes.config';
import {environment} from '../../environments/environment';
import {ReloadPageModalComponent} from '../components/reload-page/reload-page-modal.component';
import {ErrorBlackListModel, ErrorBlackListToken} from '@shared-models/common.models';
import {ModalOptions} from 'ngx-bootstrap/modal';


@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    constructor(
        private router: Router,
        private authService: AuthService,
        private customModalService: CustomModalService,
        @Optional() @Inject(ErrorBlackListToken) private errorBlackList: ErrorBlackListModel[],
    ) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.authService.isTokenExpired) {
            return this.doInterception(req, next);
        } else {

            // must return token
            return from(this.authService.authorizeUser())
                .pipe(
                    switchMap(() => {
                        return this.doInterception(req, next);
                    })
                );

        }
    }

    private doInterception(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const accessToken: string = localStorage.getItem(Models.CE.StorageKeys.accessToken);

        if (accessToken && this.shouldBeAuthorized(req)) {
            req = req.clone({
                setHeaders: {
                    Authorization: 'Bearer ' + accessToken
                }
            });
        }

        return next.handle(req).pipe(
            catchError((err: HttpErrorResponse) => {
                if (this.isNotInterceptedError(err)) {
                    return throwError(err);
                }

                switch (err.status) {
                    case ResponseStatus.Conflict: // 409
                        if (err.message === 'Unauthorized') {
                            this.showReloadModal();
                        }
                        break;
                    case ResponseStatus.NotFound: // 404
                        this.customModalService.closeAllModals();
                        this.router.navigate([GeneralRoutesConfig.general.notFound]);
                        break;
                    case ResponseStatus.Gone: // 410
                        this.router.navigate([GeneralRoutesConfig.general.notValidPage]);
                        break;
                    case ResponseStatus.Forbidden: // 403
                        this.forbiddenHandler(err);
                        break;
                }

                return throwError(err);
            })
        );
    }

    private forbiddenHandler(errRes: HttpErrorResponse): void {
        if (errRes.error && errRes.error.reason === 'accessDenied') {
            const accessDeniedUrl = [GeneralRoutesConfig.general.accessDeniedPage];
            const relatedBlackListItem = this.errorBlackList.find(rule => rule.status === errRes.status);

            if (relatedBlackListItem && relatedBlackListItem.url) {
                accessDeniedUrl.unshift(relatedBlackListItem.url);
            }

            this.router.navigate(accessDeniedUrl);
            return;
        }

        // if (errRes.error.message.includes('User is not authorized to access this resource')) {
        //     this.businessService.localStorageService.removeItem('impersonateToken');
        //     this.businessService.localStorageService.removeItem('accessToken');
        //     this.businessService.adalService.logout();
        // }
        this.showReloadModal();
    }

    private showReloadModal(): void {
        const modalParams = { title: 'Session expired', applyText: 'Reload' };
        const modalConfig: ModalOptions = { backdrop: true, ignoreBackdropClick: true };
        this.customModalService
            .initModal(ReloadPageModalComponent, modalParams, 'expiration-modal', modalConfig)
            .subscribe();

        /*  if (this.businessService.userProfileService.user) {
              this.businessService.userProfileService.user = null;
              this.businessService.customModalService.closeAllModals();
              this.businessService.userProfileService.getUserData().subscribe();
          }*/
    }

    private isNotInterceptedError(err: HttpErrorResponse): boolean {
        return this.errorBlackList.some(item => err.status === item.status && err.url.includes(item.url));
    }

    private shouldBeAuthorized(req: HttpRequest<any>): boolean {
        return [
            environment.baseUrl,
            environment.baseFileServiceUrl
        ].some(url =>
            req.url.includes(url));
    }
}

export const AUTH_PROVIDER: ClassProvider = {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true
};
